import { combineReducers } from 'redux';
import { ActionType } from 'typesafe-actions';
import * as models from '../../models';
import * as actions from './actions';
import * as constants from './constants';

export interface CompaniesStateShape {
    company: models.Company; 
    companiesRequest: models.CompaniesRequest;
    companiesResponse: models.CompaniesResponse;
    insCompaniesList: models.CompaniesResponse; 
    isCompanyLoading: boolean;
    isCompanyListLoading: boolean; 
    isCompanyListDownloading: boolean;
    isCompanyFileUploading: boolean; 
    isInsCompanyListLoading: boolean; 
    isDeletingCompanyFile: boolean;
 }

export const companiesDefaultState: CompaniesStateShape = {
    company: models.defaultCompany, 
    companiesRequest: models.defaultCompanyRequest,
    companiesResponse: models.defaultResponse,     
    insCompaniesList: models.defaultResponse, 
    isCompanyLoading: false, 
    isCompanyListLoading: false,    
    isCompanyListDownloading: false,
    isCompanyFileUploading: false,
    isInsCompanyListLoading: false, 
    isDeletingCompanyFile: false,
};
export interface CompanyFileTypesStateShape {
    companyFileTypesList: models.CompanyFileTypeResponse;    
    companyFileTypeSections: models.DocumentSectionMeta[];
    isCompanyFileTypesLoading: boolean; 
    isAddingCompanyFileType: boolean;
    isUpdatingCompanyFileType: boolean;    
}

export const companyFileTypesDefaultState: CompanyFileTypesStateShape = {
    companyFileTypesList: models.defaultResponse,
    companyFileTypeSections: [],
    isCompanyFileTypesLoading: false, 
    isAddingCompanyFileType: false,
    isUpdatingCompanyFileType: false,    
}

export interface FormFilters {
    allCompaniesDD: models.DropdownListResponse, 
    agentCompanyDD: models.DropdownListResponse, 
    companyDD: models.DropdownListResponse; 
    companyFileTypesDD: models.DropdownListResponse;
    insCompanyDD: models.DropdownListResponse; 
    isAllCompanyDDLoading: boolean; 
    isAgentCompanyDDLoading: boolean; 
    isCompanyDDLoading: boolean; 
    isCompanyFileTypesDDLoading: boolean;
    isInsCompanyDDLoading: boolean; 
    isMortgageCompanyDDLoading: boolean; 
    mortgageCompanyDD: models.DropdownListResponse; 
}

export const FormFilterDefaultState: FormFilters = {
    allCompaniesDD: models.defaultResponse, 
    agentCompanyDD: models.defaultResponse, 
    companyDD: models.defaultResponse, 
    companyFileTypesDD: models.defaultResponse,
    insCompanyDD: models.defaultResponse, 
    isAllCompanyDDLoading: false, 
    isAgentCompanyDDLoading: false, 
    isCompanyDDLoading: false, 
    isCompanyFileTypesDDLoading: false,
    isInsCompanyDDLoading: false,
    isMortgageCompanyDDLoading: false, 
    mortgageCompanyDD: models.defaultResponse, 
}

export interface State {    
    company: CompaniesStateShape;
    companyFileTypes: CompanyFileTypesStateShape;
    dropdowns: FormFilters; 
}

export type Action = ActionType<typeof actions>;

export default combineReducers<State, Action>({
    company: (state = companiesDefaultState, action) => {
        switch (action.type) {            
            case constants.RECEIVE_COMPANY:
                return {
                    ...state, 
                    company: action.payload.company,
                    isCompanyLoading: false, 
                }; 

            case constants.REQUEST_COMPANY:
                return {
                    ...state,
                    company: models.defaultCompany,
                    companyId: action.payload.companyId,
                    isCompanyLoading: true, 
                }; 

            case constants.REQUEST_CREATE_COMPANY:
                return {
                    ...state,
                    isCompanyLoading: true, 
                };
            case constants.REQUEST_CREATE_COMPANY_SUCCESS:
                return {
                    ...state,
                    isCompanyLoading: false,
                };
            case constants.REQUEST_CREATE_COMPANY_FAILED:
                return {
                    ...state,
                    isCompanyLoading: false, 
                };  

            case constants.REQUEST_COMPANY_LIST:
                return {
                    ...state, 
                    companiesResponse: models.defaultResponse,
                    isCompanyListLoading: true, 
                    companiesRequest: action.payload.request,
                }; 

            case constants.RECEIVE_COMPANY_LIST: 
                return {
                    ...state,
                    companiesResponse: action.payload.response, 
                    isCompanyListLoading: false, 
                }; 

            case constants.REQUEST_UPLOAD_COMPANY_FILE: 
                return {
                    ...state,
                    isCompanyFileUploading: true,
                };
            case constants.REQUEST_UPLOAD_COMPANY_FILE_SUCCESS:
                return {
                    ...state,
                    isCompanyFileUploading: false,
                };
            case constants.REQUEST_UPLOAD_COMPANY_FILE_FAILED:
                return {
                    ...state,
                    isCompanyFileUploading: false,
                };

            case constants.REQUEST_INSURANCE_COMPANY_LIST: 
                return {
                    ...state, 
                    isInsCompanyListLoading: true,
                };

            case constants.RECEIVE_INSURANCE_COMPANY_LIST: 
                return {
                    ...state,
                    isInsCompanyListLoading: false, 
                    insCompaniesList: action.payload.response,
                }; 

            case constants.REQUEST_COMPANY_UPDATE:
                return {
                    ...state, 
                    isCompanyLoading: true,
                }; 
            case constants.REQUEST_COMPANY_UPDATE_SUCESS:                
                return {
                    ...state,
                    isCompanyLoading: false, 
                };
            case constants.REQUEST_COMPANY_UPDATE_FAILED:
                return {
                    ...state, 
                    isCompanyLoading: false, 
                };
            case constants.REQUEST_DELETE_COMPANY_FILE:
                return {
                    ...state,
                    isDeletingCompanyFile: true,
                };
            case constants.REQUEST_DELETE_COMPANY_FILE_SUCCESS:
                return {
                    ...state,
                    isDeletingCompanyFile: false,
                };
            case constants.REQUEST_DELETE_COMPANY_FILE_FAILED:
                return {
                    ...state,
                    isDeletingCompanyFile: false,
                };
            default: 
                return state; 
        }
    }, 
    companyFileTypes: (state = companyFileTypesDefaultState, action) => {
        switch (action.type) {
            case constants.REQUEST_COMPANY_FILE_TYPES_LIST:
                return {
                    ...state, 
                    isCompanyFileTypesLoading: true,
                }; 
            case constants.RECEIVE_COMPANY_FILE_TYPES_LIST: 
                return {
                    ...state,
                    isCompanyFileTypesLoading: false,
                    companyFileTypesList: action.payload.response, 
                };
            case constants.REQUEST_ADD_COMPANY_FILE_TYPE:
                return {
                    ...state,
                    isAddingCompanyFileType: true,
                }; 
            case constants.REQUEST_ADD_COMPANY_FILE_TYPE_SUCCESS:                   
                return {
                    ...state,
                    isAddingCompanyFileType: false,
                };
            case constants.REQUEST_ADD_COMPANY_FILE_TYPE_FAILED:
                return {
                    ...state,
                    isAddingCompanyFileType: false, 
                };
            case constants.REQUEST_COMPANY_UPDATE_FILE_TYPE:
                return {
                    ...state,
                    isUpdatingCompanyFileType: true,
                };
            case constants.REQUEST_COMPANY_UPDATE_FILE_TYPE_SUCCESS:
                return {
                    ...state,
                    isUpdatingCompanyFileType: false, 
                };
            case constants.REQUEST_COMPANY_UPDATE_FILE_TYPE_FAILED:
                return {
                    ...state,
                    isUpdatingCompanyFileType: false, 
                };

            case constants.REQUEST_FILE_TYPE_SECTIONS:
                return {
                    ...state,
                    companyFileTypeSections: [],
                };
            case constants.RECEIVE_FILE_TYPE_SECTIONS:  
                return {
                    ...state,
                    companyFileTypeSections: action.payload.sections,
                };                

            default:
                return state;
        }
    },
    dropdowns: (state = FormFilterDefaultState, action) => {
        switch (action.type) {
            case constants.REQUEST_ALL_COMPANIES_DD: 
                return {                    
                    ...state,
                    isAllCompanyDDLoading: true, 
                }; 
            case constants.RECEIVE_ALL_COMPANIES_DD: 
                return {
                    ...state, 
                    allCompaniesDD: action.payload.response, 
                    isAllCompanyDDLoading: false, 
                }
            case constants.REQUEST_AGENT_COMPANY_DD: 
                return {
                    ...state, 
                    isAgentCompanyDDLoading: true, 
                }; 
            case constants.RECEIVE_AGENT_COMPANY_DD: 
                return {
                    ...state,
                    isAgentCompanyDDLoading: false, 
                    agentCompanyDD: action.payload.response, 
                }; 

            case constants.REQUEST_COMPANY_DROPDOWN: 
                return {
                    ...state,
                    isCompanyDDLoading: true, 
                }; 
            case constants.RECEIVE_COMPANY_DROPDOWN: 
                return {
                    ...state,
                    isCompanyDDLoading: false, 
                    companyDD: action.payload.response, 
                }; 

            case constants.REQUEST_INS_COMPANY_DD: 
                return {
                    ...state,
                    isInsCompanyDDLoading: true,
                }; 

            case constants.RECEIVE_INS_COMPANY_DD: 
                return {
                    ...state, 
                    isInsCompanyDDLoading: false, 
                    insCompanyDD: action.payload.response, 
                }; 

            case constants.REQUEST_MORTGAGE_COMPANY_DD: 
                return {
                    ...state,
                    isMortgageCompanyDDLoading: true, 
                }; 
            case constants.RECEIVE_MORTGAGE_COMPANY_DD: 
                return {
                    ...state,
                    mortgageCompanyDD: action.payload.response, 
                    isMortgageCompanyDDLoading: false, 
                }; 
            case constants.REQUEST_COMPANY_FILE_TYPES_DD:
                return {
                    ...state,
                    isCompanyFileTypesDDLoading: true,
                };
            case constants.RECEIVE_COMPANY_FILE_TYPES_DD:
                return {
                    ...state,
                    isCompanyFileTypesDDLoading: false,
                    companyFileTypesDD: action.payload.response,
                };
            default: 
                return state; 
        }
    }, 
});
